'use client';
import React from 'react';
import Image from 'next/image';
import { useCookies } from 'next-client-cookies';
import { Product } from 'shared/types/product';
import Link from 'components/commercetools-ui/atoms/link';
import { ChangeStoreIcon } from 'components/icons';
import { trackCtaClick } from 'helpers/analytics/ga4Events';
import { M2H_SHIPPING_METHOD_KEY } from 'helpers/constants/cart';
import useImageSizes from 'helpers/hooks/useImageSizes';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { getAttributeValueLowerCase } from 'helpers/utils/attributes';
import { getCustomizationData, setCustomizationData } from 'helpers/utils/customizationStorage';
import { desktop, mobile, tablet } from 'helpers/utils/screensizes';
import { useRouter } from 'i18n/routing';
import { getLocalizationInfo } from 'project.config';
import { sdk } from 'sdk';
import { useAccount, useCart } from 'frontastic';

export interface Props {
  product: Product;
  keyprop: number;
  previewLabel?: string;
  saveToProjects?: string;
  beautyshot?: string | null;
  successMessageText?: string;
  errorMessageText?: string;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
  data?: any;
  tileBgColor?: string;
  tileImgContainerBgColor?: string;
  sourceProjectId?: string;
}

export default function Tile({
  product,
  keyprop,
  previewLabel,
  beautyshot,
  data,
  tileBgColor,
  tileImgContainerBgColor,
  sourceProjectId,
}: Readonly<Props>) {
  const [isDesktopSize] = useMediaQuery(desktop);
  const [, width] = useMediaQuery(mobile);
  const isPureMobile = width < tablet;
  const router = useRouter();
  const { miniCarts } = useCart();
  const { loggedIn } = useAccount();
  const extensions = sdk.composableCommerce;
  const cookies = useCookies();
  const locale = cookies.get('locale') as string;
  const { region } = getLocalizationInfo(locale);

  const productCategories = product?.categories || [];
  const tileImageSizes = useImageSizes({ sm: 0.15, md: 0.15, lg: 0.15, defaultSize: 0.15, maxWidth: 240 });
  // Helper to check if a product category belongs to a root category's tree
  /* const findMatchingRootCategory = () => {
    if (!categoryBarData || !productCategories.length) return undefined;

    for (const root of categoryBarData) {
      for (const pCat of productCategories) {
        const pCatName = pCat.name?.toLowerCase();

        // 1. Check if it's the root itself
        if (root.categoryName?.toLowerCase() === pCatName) return root;

        // 2. Check subcategories
        const isChild = root.subcategoryOptions?.some((sub) => {
          if (sub.subcategoryName?.toLowerCase() === pCatName) return true;

          // 3. Check sub-subcategories
          return sub.subSubCategoryOptions?.some((ssub) => ssub.subSubCategoryName?.toLowerCase() === pCatName);
        });

        if (isChild) return root;
      }
    }
    return null;
  }; */

  const sharedData = async (item?: any, token?: string) => {
    const customizationData = getCustomizationData();

    const orientation = getAttributeValueLowerCase(item?.variants?.[0]?.attributes, 'orientation');
    let orientationValue = '';
    if (orientation === 'both' || orientation === 'landscape' || orientation === 'horizontal') {
      orientationValue = `${item?.variants?.[0]?.attributes?.mediaclip_product_id_universal}-L`;
    } else if (orientation === 'portrait' || orientation === 'vertical') {
      orientationValue = `${item?.variants?.[0]?.attributes?.mediaclip_product_id_universal}-P`;
    } else {
      orientationValue = `${item?.variants?.[0]?.attributes?.mediaclip_product_id_universal}`;
    }

    const payload = {
      module: item?.variants?.[0]?.attributes?.mediaclip_module_universal,
      productId: orientationValue,
      themeUrl: item?.attributes?.mediaclip_theme_url_universal,
      externalProductId: item?.variants[0]?.sku,
      token: token,
      title: customizationData?.title,
      ...(sourceProjectId ? { sourceProjectId } : {}),
    };

    const res = await extensions.mediaClip.createProject(payload);
    const createdProjectId = res?.isError ? '' : res?.data?.projectId;
    return createdProjectId;
  };

  const getToken = async () => {
    if (loggedIn) {
      const resMediaClipToken = await extensions.mediaClip.getUserToken();
      if (!resMediaClipToken.isError && globalThis.window !== undefined) {
        const mediaClipToken = resMediaClipToken?.data?.userToken;
        const rawCustomizationData = getCustomizationData();
        const cart = miniCarts?.find(
          (miniCart) =>
            miniCart.fulfillmentType &&
            (rawCustomizationData?.distributionChannelKey as string)
              ?.toLowerCase()
              ?.includes(miniCart?.fulfillmentType),
        );
        const finalData = {
          ...rawCustomizationData,
          token: mediaClipToken,
          cartId: cart?.cartId ?? '',
          distributionChannelKey: M2H_SHIPPING_METHOD_KEY[region],
          shippingAddress: '',
          shippingMethod: '',
          sku: product?.variants?.[0]?.sku,
          productName: product?.variants?.[0]?.attributes?.display_name_universal ?? product?.name,
          categories: productCategories.map((cat) => cat.name),
          type: 'start',
        };
        setCustomizationData(finalData);
        return mediaClipToken;
      } else {
        console.error('Error fetching user token:', resMediaClipToken.isError);
        return;
      }
    }
  };
  const handlePreview = async (item: any) => {
    const token = await getToken();

    const createdProjectId = await sharedData(item, token);
    if (createdProjectId) {
      router.push(`/designer/mediaclip/${createdProjectId}`);
    }
  };

  /* const saveProject = async (item?: Product) => {
    try {
      const token = await getToken();
      const createdProjectId = await sharedData(item, token);

      const expirationDate = new Date(new Date().setDate(new Date().getDate() + mediaclipExpireInDays)).toISOString();

      const wishlistLineItem = {
        variant: { sku: item?.variants[0]?.sku },
        count: 1,
        custom: {
          type: {
            typeId: 'type',
            key: 'lineitem_ext',
          },
          fields: {
            mediaClipProjectId: createdProjectId,
            expirationDate,
          },
        },
      };

      await addToWishlist(wishlist as Wishlist, wishlistLineItem as any, 1);
      onSuccess?.((data?.successMessage as string) || (successMessageText as string));

      // GA4: project_save (Save from Banner Tile)
      try {
        trackProjectSave({
          eventName: "project_save",
          projectId: createdProjectId,
          projectProductIdList: item?.variants?.[0]?.sku,
          projectProductNameList: item?.name,
          projectProductCategoryList: productCategories[0]?.name,
          projectProductCategory2List: productCategories[1]?.name,
          projectProductCategory3List: productCategories[2]?.name,
          projectExpiryDate: expirationDate,
          projectBuilder: 'Mediaclip',
        });
      } catch (gaError) {
        console.error('GA4 project_save error', gaError);
      }
    } catch (error) {
      console.error('Add to wishlist failed', error);
      onError?.((data?.errorMessage as string) || (errorMessageText as string));
    }
  }; */

  return (
    <div
      className={`flex justify-between rounded-2xl p-16 lg:p-24 pl-24 lg:pl-32 hover-lift-parent lift-target ${!isDesktopSize && 'mb-20'}`}
      data-testid="personalized-banner-tile"
      data-ffid={`ctr-personalized-banner-tile-${keyprop}`}
      style={{ backgroundColor: tileBgColor ?? '#F8F9FA' }}
    >
      <div className="flex flex-col justify-between">
        <div>
          <div className="md:mb-16 mb-5 flex items-center gap-8 text-[#018463]">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
            </svg>
            <span
              className="md:text-14 text-12 leading-none sm:font-medium"
              data-testid="tile-category-name"
              style={{ transform: 'translateY(1px)' }}
              data-ffid={`lbl-tile-category-name-${keyprop}`}
            >
              {product?.variants?.[0]?.attributes?.short_description_universal || data?.tileSubtext || ''}
            </span>
          </div>
          <h3
            className="line-36 block text-black max-w-200 leading-tight"
            style={{
              fontSize: isPureMobile
                ? `${data?.tileTitleFontSizeMobile || '20'}px`
                : `${data?.tileTitleFontSize || '28'}px`,
              color: data?.tileTitleTextColor || '#000',
            }}
          >
            {product?.name || data?.tileTitle}
          </h3>
        </div>
        <Link
          className="flex items-center pl-2 text-14 text-[#0B57D0] sm:mt-20 sm:text-16 md:text-20"
          // link="#"
          data-testid="tile-preview-link"
          data-ffid={`lnk-tile-preview-${keyprop}`}
          onClick={() => {
            trackCtaClick('personalized_banner', previewLabel ?? 'Preview', product?.slug ? `/p/${product.slug}` : '#');
            void handlePreview(product);
          }}
        >
          {previewLabel ?? 'Preview'}
          <ChangeStoreIcon className="ml-4 inline-block w-14" />
        </Link>
      </div>

      <div className="ml-20 flex items-center">
        <div
          className="overflow-hidden rounded-[20px] p-12"
          style={{ backgroundColor: tileImgContainerBgColor ?? '#FFF' }}
        >
          {/* product image */}
          {beautyshot == null ? (
            <button className="relative block" onClick={() => handlePreview(product)}>
              <Image
                src={product?.variants?.[0]?.images?.[0] || data?.imageSrc}
                alt={product?.name || data?.imageAlt}
                data-ffid={`img-personalized-tile-${keyprop}`}
                className={`${isDesktopSize ? 'size-180 min-w-180 min-h-180' : 'size-120 min-w-120 min-h-120'} object-contain`}
                sizes={tileImageSizes}
                unoptimized={true}
                width={isDesktopSize ? 180 : 120}
                height={isDesktopSize ? 180 : 120}
              />
            </button>
          ) : (
            <>
              {beautyshot ? (
                <button onClick={() => handlePreview(product)} className="block">
                  <Image
                    src={beautyshot}
                    alt={product?.name ?? ''}
                    className={`${isDesktopSize ? 'size-180 min-w-180 min-h-180' : 'size-120 min-w-120 min-h-120'} object-contain`}
                    data-testid={'curated-tile-image'}
                    data-ffid={`img-personalized-tile-${keyprop}`}
                    unoptimized={true}
                    width={isDesktopSize ? 180 : 120}
                    height={isDesktopSize ? 180 : 120}
                  />
                </button>
              ) : (
                <div
                  className="flex size-124 items-center justify-center sm:size-175 md:size-208"
                  data-testid="tile-loader"
                >
                  <div className="size-24 animate-spin rounded-full border-2 border-[#01916D] border-t-transparent" />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
